import ResizeHandle from '@/components/common/ResizeHandle';
import { useSocket } from '@/context/socketContext';
import useNotify from '@/hooks/useNotify';
import componentRegistry from '@/lib/componentRegistry';
import { closeWindow, focusWindow, hideWindow, maximizeWindow, minimizeWindow, updateWindowPosition, updateWindowSize } from '@/redux/slice/windowSlice';
import { RootState, useAppSelector } from '@/redux/store';
import { MOBILE_VIEW_BREAKPOINT } from '@/utils/constant';
import { SocketUser, WinDataType, WindowData } from '@/utils/types/window';
import { animated, useSpring } from '@react-spring/web';
import { useSession } from 'next-auth/react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiMinus } from 'react-icons/fi';
import { IoClose, IoResize } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
interface WindowProps {
  id: number;
  title: string;
  componentKey: string;
  isMinimized: boolean;
  isMaximized: boolean;
  isWindowOpen: boolean;
  isFocused: boolean;
  headerHeight: number;
  footerHeight: number;
  winData?: WinDataType;
  disableClose?: boolean;
  disableMessage?: string[];
  zIndex: number;
}
const Window: React.FC<WindowProps> = ({
  id,
  title,
  componentKey,
  isMinimized,
  isMaximized,
  isWindowOpen,
  isFocused,
  headerHeight,
  footerHeight,
  winData,
  disableClose,
  disableMessage,
  zIndex
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isComponentLoading, setIsComponentLoading] = useState(false);
  const {
    socket,
    listenToModuleConnection,
    joinModule,
    leaveModule,
    getUsersInRoom
  } = useSocket();
  const {
    data: session
  } = useSession();
  const [showMoreUsers, setShowMoreUsers] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  // const isMobile = window.innerWidth <= 1024;
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const {
    NotifyComponent,
    triggerNotify
  } = useNotify();
  const isImpersonating = useAppSelector(state => state.token.isImpersonating);

  // Add beforeClose ref
  const beforeCloseRef = useRef<(() => Promise<void>) | null>(null);
  const user = useMemo(() => ({
    id: session?.user?.id ?? '',
    name: session?.user?.name ?? '',
    email: session?.user?.email ?? '',
    shortName: session?.user?.name ? session.user.name.substring(0, 2).toUpperCase() : ''
  }), [session?.user?.id, session?.user?.name, session?.user?.email]);
  // ? DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE
  const [_socketUserList, setSocketUserList] = useState<SocketUser[]>([]);
  const dispatch = useDispatch();
  const windowData = useSelector((state: RootState) => state.windows.windows.find((w: WindowData) => w.id === id));
  const winHeight = isImpersonating ? window.innerHeight - 110 : window.innerHeight - 85;
  const defaultValues = {
    position: {
      x: 0,
      y: 0
    },
    size: {
      width: 840,
      height: winHeight
    },
    isAdjustable: true,
    isDraggable: true
  };
  const {
    position,
    size,
    isAdjustable,
    isDraggable
  } = {
    ...defaultValues,
    ...windowData,
    size: {
      width: windowData?.size?.width ?? defaultValues.size.width,
      height: windowData?.size?.height === 0 ? winHeight : windowData?.size?.height ?? 680
    }
  };
  const [localPosition, setLocalPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({
    x: 0,
    y: 0
  });
  const allowAdjust = useMemo(() => typeof isAdjustable !== 'undefined' && (typeof isAdjustable === 'boolean' ? isAdjustable : true), [isAdjustable]);
  const allowDrag = useMemo(() => typeof isDraggable !== 'undefined' && (typeof isDraggable === 'boolean' ? isDraggable : true), [isDraggable]);
  const isMobileView = window.innerWidth <= MOBILE_VIEW_BREAKPOINT;
  useEffect(() => {
    if (!isDragging) {
      setLocalPosition(position);
    }
  }, [position, isDragging]);
  const handleAssetSelection = useCallback((assetId: string) => {
    setSelectedAssetId(assetId);
  }, [setSelectedAssetId]);
  useEffect(() => {
    if (!selectedAssetId || !socket || !session) {
      return () => {};
    }
    joinModule(selectedAssetId, user as SocketUser);
    return () => {
      leaveModule(selectedAssetId, user as SocketUser);
    };
  }, [selectedAssetId, socket, session, joinModule, user, leaveModule]);
  useEffect(() => {
    if (selectedAssetId) {
      getUsersInRoom(selectedAssetId, session?.user?.name ?? '', setSocketUserList);
    }
  }, [getUsersInRoom, selectedAssetId, session?.user?.name]);
  useEffect(() => {
    if (!selectedAssetId || !socket || !session?.user?.name) {
      return;
    }
    listenToModuleConnection(selectedAssetId, session?.user?.name ?? '', setSocketUserList);
  }, [socket, session?.user?.name, selectedAssetId, listenToModuleConnection]);

  // ? DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE
  // const getUserColor = useCallback((userId: string) => {
  //   let hash = 0;
  //   for (let i = 0; i < userId.length; i++) {
  //     hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  //   }

  // ? DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE
  //   const hue = Math.abs(hash % 360);
  //   const saturation = 70;
  //   const lightness = 60;
  //   const textLightness = 20;
  //   return {
  //     bgColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
  //     textColor: `hsl(${hue}, ${saturation}%, ${textLightness}%)`,
  //   };
  // }, []);

  // ? DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE
  // const userColors = useMemo(() => {
  //   return socketUserList?.map((u) => {
  //     const color = getUserColor(u.id);
  //     return {
  //       id: u.id,
  //       textColor: color.textColor,
  //       bgColor: color.bgColor,
  //     };
  //   });
  // }, [socketUserList, getUserColor]);

  const allWindows = useSelector((state: RootState) => state.windows.windows);
  const Component = componentRegistry[componentKey];
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const [browserWidth, setBrowserWidth] = useState(window.innerWidth);
  const [browserHeight, setBrowserHeight] = useState(window.innerHeight);
  const handleMouseDownDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!allowDrag) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dispatch(focusWindow(id));
    if ('touches' in e) {
      const touchEvent = e;
      if (touchEvent.touches.length > 0) {
        const firstTouch = touchEvent.touches[0];
        dragOffset.current = {
          x: firstTouch.clientX - localPosition.x,
          y: firstTouch.clientY - localPosition.y
        };
      }
    } else {
      const mouseEvent = e;
      dragOffset.current = {
        x: mouseEvent.clientX - localPosition.x,
        y: mouseEvent.clientY - localPosition.y
      };
    }
  };
  const handleMouseMoveDrag = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging || isMaximized || !allowDrag) return;
    e.preventDefault();
    let clientX = 0;
    let clientY = 0;
    if (e instanceof MouseEvent) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e instanceof TouchEvent && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      return;
    }
    requestAnimationFrame(() => {
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height - footerHeight;
      const newX = Math.max(0, Math.min(clientX - dragOffset.current.x, maxX));
      const newY = Math.max(headerHeight, Math.min(clientY - dragOffset.current.y, maxY));
      setLocalPosition({
        x: newX,
        y: newY
      });
    });
  }, [isDragging, isMaximized, size.width, size.height, allowDrag, headerHeight, footerHeight]);
  const handleMouseUpDrag = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dispatch(updateWindowPosition({
        id,
        position: localPosition
      }));
    }
  }, [isDragging, localPosition, dispatch, id]);
  const handleMinimize = useCallback(() => {
    dispatch(minimizeWindow(id));
  }, [dispatch, id]);
  const handleMaximize = useCallback(() => {
    dispatch(maximizeWindow(id));
  }, [dispatch, id]);

  // Updated handleClose function with beforeClose support
  const handleClose = useCallback(async () => {
    if (disableClose) {
      if (disableMessage && disableMessage?.length > 0) {
        triggerNotify({
          notifyType: 'warning',
          title: 'WARNING',
          notifyMessage: <>
              {disableMessage.map((i, idx) => <React.Fragment key={i}>
                  {i}
                  {disableMessage.length - 1 !== idx ? <br /> : null}
                </React.Fragment>)}
            </>
        });
      }
      return;
    }
    try {
      // Call the beforeClose function if it exists
      if (beforeCloseRef.current) {
        const result = await beforeCloseRef.current();
        if (typeof result === 'boolean' && result === false) {
          return;
        }
      }
      if (selectedAssetId && user.id) {
        leaveModule(selectedAssetId, user as SocketUser);
      }
      dispatch(closeWindow(id));
    } catch (error) {
      console.error('Error during window close:', error);
      if (selectedAssetId && user.id) {
        leaveModule(selectedAssetId, user as SocketUser);
      }
      dispatch(closeWindow(id));
    }
  }, [dispatch, id, selectedAssetId, user, leaveModule]);
  const handleHide = useCallback(() => {
    dispatch(hideWindow(id));
  }, [dispatch, id]);
  const handleMouseDownResize = useCallback((direction: string) => (e: React.MouseEvent | React.TouchEvent) => {
    if (!allowAdjust) return;
    dispatch(focusWindow(id));
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  }, [allowAdjust, dispatch, id]);
  const handleMouseMoveResize = useCallback((e: MouseEvent) => {
    if (!isResizing || isMaximized || !allowAdjust) {
      return;
    }
    let {
      width,
      height
    } = size;
    let {
      x,
      y
    } = position;
    const minWidth = 200;
    const minHeight = 200;
    const maxY = window.innerHeight - footerHeight;
    const limitValue = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
    const resizeHandlers: Record<string, () => void> = {
      'top-left': () => {
        width = limitValue(size.width - (e.clientX - position.x), minWidth, size.width);
        height = limitValue(size.height - (e.clientY - position.y), minHeight, size.height);
        x = limitValue(e.clientX, 0, position.x + size.width - minWidth);
        y = limitValue(e.clientY, headerHeight, position.y + size.height - minHeight);
      },
      'top-right': () => {
        width = Math.max(e.clientX - position.x, minWidth);
        height = limitValue(size.height - (e.clientY - position.y), minHeight, size.height);
        y = limitValue(e.clientY, headerHeight, position.y + size.height - minHeight);
      },
      'bottom-left': () => {
        width = limitValue(size.width - (e.clientX - position.x), minWidth, size.width);
        height = limitValue(e.clientY - position.y, minHeight, maxY - position.y);
        x = Math.max(0, e.clientX);
      },
      'bottom-right': () => {
        width = Math.max(e.clientX - position.x, minWidth);
        height = limitValue(e.clientY - position.y, minHeight, maxY - position.y);
      },
      top: () => {
        height = limitValue(size.height - (e.clientY - position.y), minHeight, size.height);
        y = limitValue(e.clientY, headerHeight, position.y + size.height - minHeight);
      },
      bottom: () => {
        height = limitValue(e.clientY - position.y, minHeight, maxY - position.y);
      },
      left: () => {
        width = limitValue(size.width - (e.clientX - position.x), minWidth, size.width);
        x = e.clientX;
      },
      right: () => {
        width = Math.max(e.clientX - position.x, minWidth);
      }
    };
    if (resizeDirection && resizeHandlers[resizeDirection]) {
      resizeHandlers[resizeDirection]();
    }
    dispatch(updateWindowSize({
      id,
      size: {
        width,
        height
      }
    }));
    dispatch(updateWindowPosition({
      id,
      position: {
        x,
        y
      }
    }));
  }, [isResizing, isMaximized, allowAdjust, size, position, resizeDirection, dispatch, id, headerHeight, footerHeight]);
  const handleMouseUpResize = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);
  useEffect(() => {
    if (!isMaximized && !isMinimized) {
      // Top Height of the window from header
      const minHeight = isImpersonating ? 170 : 120;
      const windowOffset = 20;
      const numberOfWindows = allWindows.length - 1;
      if (position.x === 0 && position.y === 0) {
        const centerX = (window.innerWidth - size.width) / 2;
        const centerY = (window.innerHeight - (window.innerHeight - minHeight)) / 2;
        if (allWindows.length === 1) {
          dispatch(updateWindowPosition({
            id,
            position: {
              x: centerX,
              y: centerY
            }
          }));
        } else {
          dispatch(updateWindowPosition({
            id,
            position: {
              x: centerX + numberOfWindows * windowOffset,
              y: centerY + numberOfWindows * windowOffset
            }
          }));
        }
      }
    }
  }, [id, dispatch, allWindows.length, position.x, position.y, isMaximized, isMinimized, size.width]);
  useEffect(() => {
    const isNormalState = !isMaximized && !isMinimized;
    if (isWindowOpen && isNormalState && position.x === 0 && position.y === 0) {
      const minWidth = 85;
      const newHeight = window.innerHeight - minWidth;
      if (size.width < 300 && size.height < 300) {
        dispatch(updateWindowSize({
          id,
          size: {
            width: 300,
            height: 300
          }
        }));
      } else {
        dispatch(updateWindowSize({
          id,
          size: {
            width: size.width,
            height: Math.min(newHeight, size.height)
          }
        }));
      }
    }
  }, [browserWidth, browserHeight, id, dispatch, isWindowOpen, isMaximized, isMinimized, position.x, position.y, size.width, size.height]);
  useEffect(() => {
    if (!allowAdjust && !allowDrag) return;
    const handleTouchMove = (e: TouchEvent) => handleMouseMoveDrag(e);
    if (isDragging && allowDrag) {
      // For dragging
      window.addEventListener('mousemove', handleMouseMoveDrag);
      window.addEventListener('mouseup', handleMouseUpDrag);
      window.addEventListener('touchmove', handleTouchMove, {
        passive: false
      });
      window.addEventListener('touchend', handleMouseUpDrag);
    } else if (isResizing && allowAdjust) {
      // For resizing
      window.addEventListener('mousemove', handleMouseMoveResize);
      window.addEventListener('mouseup', handleMouseUpResize);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMoveDrag);
      window.removeEventListener('mousemove', handleMouseMoveResize);
      window.removeEventListener('mouseup', handleMouseUpDrag);
      window.removeEventListener('mouseup', handleMouseUpResize);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUpDrag);
    };
  }, [isDragging, isResizing, allowDrag, allowAdjust, handleMouseMoveDrag, handleMouseUpDrag, handleMouseMoveResize, handleMouseUpResize]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setBrowserWidth(window.innerWidth);
      setBrowserHeight(window.innerHeight);
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  const springProps = useSpring({
    from: {
      opacity: 0,
      scale: 0.2
    },
    to: {
      opacity: isWindowOpen ? 1 : 0,
      scale: isWindowOpen ? 1 : 0.2,
      clipPath: isWindowOpen ? 'polygon(0 100%, 100% 100%, 100% 0, 0 0)' : 'polygon(50% 100%, 50% 100%, 50% 100%, 50% 100%)',
      transformOrigin: 'bottom center'
    },
    config: {
      tension: 250,
      friction: 22
    },
    opacity: 1
  });

  // ? DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE
  // const handleToggleMoreUsers = () => {
  //   setShowMoreUsers(!showMoreUsers);
  // };

  // Detect mobile devices
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowMoreUsers(false);
      }
    };
    if (showMoreUsers) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMoreUsers]);
  const getComputedLeft = () => {
    if (isMobileView) {
      return '15px';
    }
    if (isMaximized) {
      return 0;
    }
    return localPosition.x; // Use local position instead of Redux position
  };
  const getComputedWidth = () => {
    if (isMobileView) {
      return 'calc(100% - 30px)';
    }
    if (isMaximized) {
      return '100%';
    }
    return `${size.width}px`;
  };
  const getComputedCursor = () => {
    if (isResizing && allowAdjust) {
      return 'se-resize';
    }
    if (isDragging && allowDrag) {
      return 'move';
    }
    return 'default';
  };
  const handlePrintWindow = useReactToPrint({
    contentRef,
    documentTitle: title
  });
  const topOffset = isImpersonating ? headerHeight + 30 : headerHeight;
  let calculatedHeight = '';
  if (isImpersonating) {
    calculatedHeight = `calc(93% - ${headerHeight}px)`;
  } else {
    calculatedHeight = `calc(96% - ${headerHeight}px)`;
  }
  return <animated.div ref={contentRef} data-window-container="true" data-window-id={id} style={{
    ...springProps,
    pointerEvents: 'auto',
    position: 'absolute',
    ...(allowDrag ? {
      top: isMaximized ? topOffset : localPosition.y,
      left: getComputedLeft()
    } : {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }),
    right: isMobileView ? '15px' : 'auto',
    width: getComputedWidth(),
    height: isMaximized ? calculatedHeight : `${size.height}px`,
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    zIndex: zIndex,
    cursor: getComputedCursor(),
    overflow: 'hidden',
    willChange: isDragging ? 'transform' : 'auto',
    border: '1px solid rgb(139 156 173)'
  }} onMouseUp={handleMouseUpDrag} onMouseLeave={handleMouseUpDrag} data-sentry-element="unknown" data-sentry-component="Window" data-sentry-source-file="Window.tsx">
      {/* Focus overlay - captures clicks when window is not focused */}
      <button type="button" className="absolute inset-0 z-[1] cursor-default border-none bg-transparent p-0 text-transparent outline-none" style={{
      pointerEvents: isFocused ? 'none' : 'auto'
    }} aria-label="Click to focus window" onMouseDown={e => {
      if (!isFocused) {
        e.preventDefault();
        dispatch(focusWindow(id));
      }
    }}>
        <span className="sr-only">Focus window</span>
      </button>
      <div className={windowData?.disableOtherInteractions ? 'relative z-[10000]' : ''} role="toolbar" tabIndex={0} style={{
      background: 'rgba(96, 121, 147, 0.97)',
      padding: '6px 10px',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
      cursor: allowDrag ? 'move' : 'default',
      display: 'flex',
      justifyContent: 'space-between',
      backdropFilter: 'blur(46.90372085571289px)',
      alignItems: 'center',
      position: 'relative',
      zIndex: '1'
    }} onTouchStart={allowDrag ? handleMouseDownDrag : undefined} onMouseDown={allowDrag ? handleMouseDownDrag : undefined}>
        <span className="pl-2 text-sm font-semibold leading-[1] text-white">{title}</span>
        <div className="flex flex-shrink-0 items-center leading-[1]">
          {/* ? <----------- DO NOT REMOVE BELOW CODE - WE HAVE HIDE SOCKET USER LIST TEMPORARILY AS PER CLIENT REQUEST, MAY NEED TO REVERT BACK IN FUTURE ------------>*/}
          {/* <div className="relative mr-3 flex items-center">
            {socketUserList
              ?.filter((socketUser, index, self) => index === self.findIndex((u) => u.id === socketUser?.id))
              .slice(0, 4)
              .map((filterUser) => {
                const userColor = userColors.find((color) => color.id === filterUser?.id);
                 return (
                  <TooltipProvider key={filterUser?.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="mr-1 h-5 w-5 rounded-3xl text-sm hover:bg-gray-100"
                          style={{
                            cursor: 'pointer',
                            backgroundColor: userColor?.bgColor,
                            color: userColor?.textColor,
                          }}
                        >
                          {filterUser?.shortName}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{filterUser?.name ?? filterUser?.shortName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
               
            {socketUserList.length > 4 && (
              <>
                {!isMobile ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-2 cursor-pointer text-xs font-semibold text-white">
                          +{socketUserList.length - 4}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="center">
                        <div className="font-medium text-black">
                          {socketUserList.slice(4).map((socketUser) => (
                            <p key={socketUser.id}>{socketUser.name}</p>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <button
                    className="ml-2 cursor-pointer text-xs font-semibold text-white"
                    onClick={handleToggleMoreUsers}
                  >
                    +{socketUserList.length - 4}
                  </button>
                )}
              </>
            )}
             {isMobile && showMoreUsers && (
              <div
                ref={popupRef}
                className="absolute right-0 top-8 z-50 w-40 rounded-md bg-white p-2"
                style={{
                  display: showMoreUsers ? 'block' : 'none',
                  minHeight: '50px',
                  overflow: 'visible',
                }}
              >
                <button
                  className="mb-1 w-full text-right font-semibold text-black"
                  onClick={() => setShowMoreUsers(false)}
                >
                  ✖
                </button>
                <div className="text-sm text-black">
                  {socketUserList.slice(4).map((socketUser) => (
                    <p key={socketUser.id} className="border-b py-1 last:border-none">
                      {socketUser.name}
                    </p>
                  ))}
                </div>
              </div>
            )}
           </div> */}
          {allowAdjust && <>
              <button onClick={handleHide} className="mr-1 h-4 w-4 rounded-3xl bg-white" style={{
            cursor: 'pointer'
          }}>
                <FiMinus className="h-4 w-4" />
              </button>
              <button className="mr-1 flex h-4 w-4 items-center justify-center rounded-3xl bg-white" onClick={isMaximized ? handleMinimize : handleMaximize} style={{
            border: 'none',
            cursor: 'pointer'
          }}>
                <IoResize className="h-3 w-3" />
              </button>
            </>}
          <button className="mr-1 flex h-4 w-4 items-center justify-center rounded-3xl bg-[#F05959]" onClick={handleClose} style={{
          border: 'none',
          cursor: 'pointer'
        }}>
            <IoClose className="h-3 w-3 fill-[#fff]" data-sentry-element="IoClose" data-sentry-source-file="Window.tsx" />
          </button>
        </div>
      </div>
      <div style={{
      padding: '0px',
      background: '#fff',
      height: `calc(100% - ${allowAdjust ? '28px' : '0px'})`,
      position: 'relative',
      boxShadow: '1px'
    }}>
        <div style={{
        padding: `10px 10px ${allowAdjust ? '0' : '10px'} 10px`,
        overflowY: 'auto',
        background: '#fff',
        height: 'calc(100% - 28px)',
        marginRight: isMaximized ? '0' : '7px'
      }}>
          {Component ? <>
              {isComponentLoading && <div className="top-50 left-50 absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-50">
                  <div className="loader h-6 w-6 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                </div>}
              <Component handlePrintWindow={handlePrintWindow} windowTitle={title} windowId={id} selectedAssetId={selectedAssetId} onAssetSelect={handleAssetSelection} isComponentLoading={isComponentLoading} setIsComponentLoading={setIsComponentLoading} winData={winData} beforeCloseRef={beforeCloseRef} // Pass the ref to the component
          disableMessage={disableMessage} isFocused={isFocused} />
              {NotifyComponent}
            </> : <div>Content not found</div>}
        </div>
      </div>
      {/* Resizable edges */}
      <ResizeHandle isMaximized={isMaximized} allowAdjust={allowAdjust} direction="top-left" onMouseDown={handleMouseDownResize('top-left')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle isMaximized={isMaximized} allowAdjust={allowAdjust} direction="top-right" onMouseDown={handleMouseDownResize('top-right')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle isMaximized={isMaximized} allowAdjust={allowAdjust} direction="bottom-left" onMouseDown={handleMouseDownResize('bottom-left')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle isMaximized={isMaximized} allowAdjust={allowAdjust} direction="bottom-right" onMouseDown={handleMouseDownResize('bottom-right')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle allowAdjust={allowAdjust} isMaximized={isMaximized} direction="top" onMouseDown={handleMouseDownResize('top')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle allowAdjust={allowAdjust} isMaximized={isMaximized} direction="bottom" onMouseDown={handleMouseDownResize('bottom')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle allowAdjust={allowAdjust} isMaximized={isMaximized} direction="left" onMouseDown={handleMouseDownResize('left')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
      <ResizeHandle allowAdjust={allowAdjust} isMaximized={isMaximized} direction="right" onMouseDown={handleMouseDownResize('right')} data-sentry-element="ResizeHandle" data-sentry-source-file="Window.tsx" />
    </animated.div>;
};
export default memo(Window);